import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  deleteToken,
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

let messagingPromise: Promise<Messaging | null> | null = null;

// FCM in the browser needs the Push API + a service worker, so it's unsupported
// in some browsers (e.g. Safari < 16, in-app webviews) and unavailable during SSR.
function getMessagingInstance(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = isSupported().then((supported) =>
      supported ? getMessaging(firebaseApp) : null
    );
  }
  return messagingPromise;
}

/**
 * Returns an FCM registration token, or null if notification permission isn't
 * granted or FCM isn't supported. Never prompts for permission — that must come
 * from a user click (see enablePushNotifications in lib/pushNotifications.ts), or
 * browsers quietly block the prompt.
 * Reuses the app's existing `/sw.js` registration, which also handles background
 * push messages, instead of registering a second service worker.
 */
export async function getFcmToken(): Promise<string | null> {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.log('[FCM] skipping token: notification permission not granted');
    return null;
  }
  if (!('serviceWorker' in navigator)) {
    console.log('[FCM] skipping token: service workers not supported');
    return null;
  }

  const messaging = await getMessagingInstance();
  if (!messaging) {
    console.log('[FCM] skipping token: messaging not supported in this browser');
    return null;
  }

  // register() is idempotent: it returns the existing registration for '/sw.js'
  // if main.tsx already registered it, rather than creating a duplicate. But it
  // resolves as soon as the registration exists, not once a worker is active —
  // PushManager.subscribe (used internally by getToken) needs an active worker,
  // so wait for `.ready` too or it fails with "no active Service Worker".
  await navigator.serviceWorker.register('/sw.js');
  const swRegistration = await navigator.serviceWorker.ready;

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
    console.log('[FCM] token retrieved', token);
    return token;
  } catch (error) {
    console.error('Failed to retrieve FCM token', error);
    return null;
  }
}

/** Deletes this browser's FCM token so it stops receiving pushes. Never throws. */
export async function deleteFcmToken(): Promise<void> {
  try {
    const messaging = await getMessagingInstance();
    if (messaging) {
      await deleteToken(messaging);
      console.log('[FCM] token deleted');
    }
  } catch (error) {
    console.error('Failed to delete FCM token', error);
  }
}

type ForegroundMessage = {
  messageId?: string;
  title?: string;
  body?: string;
  data?: Record<string, string>;
};

const foregroundListeners = new Set<(payload: ForegroundMessage) => void>();
let foregroundHandlerAttached = false;

function dispatchForegroundMessage(payload: MessagePayload) {
  console.log('[FCM] foreground message', payload);
  const message: ForegroundMessage = {
    messageId: payload.messageId,
    title: payload.notification?.title,
    body: payload.notification?.body,
    data: payload.data,
  };
  foregroundListeners.forEach((listener) => listener(message));
}

/**
 * Subscribes to foreground push messages (i.e. received while the app tab is
 * focused). Background messages are handled separately in `public/sw.js`.
 *
 * Firebase keeps only one onMessage handler and its unsubscribe clears whatever
 * handler is current, so a stale unsubscribe (e.g. from React StrictMode's double
 * mount) would silently kill the live one. Attach Firebase's handler once and
 * fan out to listeners instead.
 */
export async function onForegroundMessage(callback: (payload: ForegroundMessage) => void) {
  foregroundListeners.add(callback);
  const unsubscribe = () => {
    foregroundListeners.delete(callback);
  };

  const messaging = await getMessagingInstance();
  if (messaging && !foregroundHandlerAttached) {
    onMessage(messaging, dispatchForegroundMessage);
    foregroundHandlerAttached = true;
  }

  return unsubscribe;
}
